import { computed, Injectable, signal, ViewContainerRef } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';

export interface SidenavOptions {
  position?: 'start' | 'end';
  focusOn?: string;
  autofocus?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SideNavService {
  elementToFocusOn: string = null;

  private readonly _content = signal(null);
  readonly content = this._content.asReadonly();
  private readonly _focus = signal(null);
  readonly focus = this._focus.asReadonly();
  isOpen = computed(() => this.panels().some((p) => this.panel(p.id).opened));
  availablePanels = computed(() => this.panels().map((panel) => panel.id));

  private panels = signal<
    {
      id: string;
      panel: MatSidenav;
      state: boolean;
    }[]
  >([]);

  public setSidenav(sidenav: MatSidenav, id: string) {
    this.panels.update((prev) => {
      const newPanels = [...prev];
      const existingPanel = newPanels.find((panel) => panel.id === id);
      if (existingPanel) {
        existingPanel.panel = sidenav;
      } else {
        newPanels.push({ id, panel: sidenav, state: false });
      }
      return newPanels;
    });
  }

  public unloadSidenav(id: string) {
    const panelIdx = this.panels().findIndex((p) => p.id === id);
    this.panels.update((prev) => {
      const newPanels = [...prev];
      newPanels.splice(panelIdx, 1);
      return newPanels;
    });
  }

  public open(
    id: string,
    template?: ViewContainerRef,
    options?: SidenavOptions,
  ): void {
    this._content.set({ id, template, options });
    this.panel(id).open();
  }

  public close(
    id: string,
    template?: ViewContainerRef,
    options?: SidenavOptions,
  ): void {
    this._content.set({ id, template, options });
    this.panel(id)?.close();
  }

  public toggle(
    id: string,
    template?: ViewContainerRef,
    options?: SidenavOptions,
  ): void {
    if (!this.panel(id)?.opened) {
      this._content.set({ id, template, options });
    }
    this.panel(id)?.toggle();
  }

  onSidenavAnimationDone(state: 'open' | 'void' | string) {
    if (state === 'open') {
      this._focus.set(this.elementToFocusOn);
    } else if (state === 'void') {
      this._focus.set(null);
    }
  }

  private panel(id) {
    return this.panels().find((panel) => panel.id === id)?.panel;
  }
}
